/**
 * Pure helpers for the platform admin screen (ADM-6): tab parsing, user-row
 * formatting, school billing seeds, and the Settings section catalogue.
 */
import type { useTranslation } from '../../../hooks/useTranslation';
import type {
  PlatformAdminTabKey,
  PlatformUser,
  School,
  SchoolBillingSeed,
  SettingsSection,
  UserAccountFilter,
} from './platformAdminTypes';

export type Translate = ReturnType<typeof useTranslation>['t'];

const PLATFORM_ADMIN_TABS: readonly PlatformAdminTabKey[] = [
  'overview',
  'schools',
  'users',
  'feedback',
  'settings',
];

/** Maps the `?tab=` route param (string or string[]) to a known tab, defaulting to overview. */
export const normalizePlatformAdminTab = (value?: string | string[]): PlatformAdminTabKey => {
  const tabParam = Array.isArray(value) ? value[0] : value;
  return PLATFORM_ADMIN_TABS.includes(tabParam as PlatformAdminTabKey)
    ? (tabParam as PlatformAdminTabKey)
    : 'overview';
};

export const filterUsersByAccountType = (
  users: PlatformUser[],
  filter: UserAccountFilter,
): PlatformUser[] =>
  users.filter((platformUser) => {
    if (filter === 'b2c') {
      return platformUser.account_type === 'b2c';
    }
    if (filter === 'b2b2c') {
      return platformUser.account_type === 'b2b2c';
    }
    return true;
  });

const capitalizeWords = (value: string) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

export const formatUserPackage = (t: Translate, userItem: PlatformUser): string => {
  if (userItem.package_label) {
    return userItem.package_label;
  }
  if (userItem.plan_type) {
    const tier = userItem.plan_tier ? ` • ${userItem.plan_tier}` : '';
    return `${userItem.plan_type}${tier}`.toUpperCase();
  }
  if (userItem.school_name) {
    return userItem.school_name;
  }
  return t('platformAdmin.userPackageUnknown');
};

export const formatUserStatus = (t: Translate, userItem: PlatformUser): string => {
  const status = userItem.subscription_status;
  if (!status) {
    return t('platformAdmin.userStatusUnknown');
  }
  // Map specific statuses to user-friendly names
  const statusMap: Record<string, string> = {
    'active': 'Active',
    'trialing': 'Trial',
    'past_due': 'Past Due',
    'canceled': 'Canceled',
    'unpaid': 'Unpaid',
    'incomplete': 'Incomplete',
    'incomplete_expired': 'Expired',
    'subscription_not_found': 'Invalid (Stale)',
  };
  return statusMap[status] || capitalizeWords(status.replace(/_/g, ' '));
};

export interface StatusPalette {
  primary: string;
  secondary: string;
  success: string;
  error: string;
}

export const getStatusColor = (palette: StatusPalette, status?: string | null): string => {
  if (!status) return palette.secondary;
  const colorMap: Record<string, string> = {
    'active': palette.success,
    'trialing': palette.primary,
    'past_due': palette.secondary,
    'canceled': palette.error,
    'unpaid': palette.error,
    'incomplete': palette.secondary,
    'incomplete_expired': palette.error,
    'subscription_not_found': '#9CA3AF', // Gray for stale subscriptions
  };
  return colorMap[status] || palette.secondary;
};

export const formatRenewalDate = (t: Translate, value?: string | null): string => {
  if (!value) {
    return t('platformAdmin.userRenewalUnknown');
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('platformAdmin.userRenewalUnknown');
  }
  return date.toLocaleDateString();
};

export const formatSubscriptionSource = (t: Translate, userItem: PlatformUser): string => {
  if (userItem.subscription_source === 'school') {
    return t('platformAdmin.userSourceSchool');
  }
  if (userItem.subscription_source === 'user') {
    return t('platformAdmin.userSourceUser');
  }
  return '';
};

export const formatUsageMinutes = (userItem: PlatformUser): string => {
  const rawSeconds = userItem.transcription_seconds_this_month;
  const minutes = typeof rawSeconds === 'number' && Number.isFinite(rawSeconds)
    ? rawSeconds / 60
    : Number(userItem.transcription_minutes_this_month ?? 0);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '0';
  }
  if (minutes < 10 && !Number.isInteger(minutes)) {
    return minutes.toFixed(1);
  }
  return Math.round(minutes).toLocaleString();
};

export const formatUsagePeriodStart = (userItem: PlatformUser): string => {
  if (!userItem.usage_period_start) {
    return 'current billing period';
  }
  const date = new Date(userItem.usage_period_start);
  if (Number.isNaN(date.getTime())) {
    return 'current billing period';
  }
  return `since ${date.toLocaleDateString()}`;
};

export const formatAnalysisUsage = (userItem: PlatformUser): string => {
  const used = userItem.transcripts_this_month || 0;
  if (userItem.school_id) {
    return `${used.toLocaleString()} by this user`;
  }
  return `${used.toLocaleString()} / ${(userItem.monthly_transcripts_limit || 0).toLocaleString()}`;
};

export const formatUsageScope = (userItem: PlatformUser): string =>
  userItem.school_id ? 'shared school pool' : 'individual account limit';

export const formatCurrency = (amountCents?: number | null, currency?: string | null) => {
  if (amountCents == null) {
    return null;
  }
  const unit = currency ? currency.toUpperCase() : 'TRY';
  return `${unit} ${(amountCents / 100).toFixed(0)}`;
};

export const getBillingSeed = (school: School): SchoolBillingSeed => ({
  currency: school.billing_currency,
  basePriceCents: school.billing_base_price_cents,
  baseSeats: school.billing_base_seats,
  addonPackageSize: school.billing_addon_package_size,
  addonPriceCents: school.billing_addon_price_cents,
});

/** Splits the comma-separated framework-key input, dropping blanks. */
export const parseFrameworkSeedKeys = (input: string): string[] =>
  input
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

export interface SettingsSectionMeta {
  key: SettingsSection;
  label: string;
  icon: string;
  description: string;
}

export const SETTINGS_SECTIONS = [
  {
    key: 'ai',
    label: 'AI Controls',
    icon: 'psychology',
    description: 'Live model routing, policies, grading behavior, and feature flags.',
  },
  {
    key: 'usage',
    label: 'Usage',
    icon: 'query-stats',
    description: 'Usage reporting notes, counter recovery, and purchasable credit packs.',
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: 'payments',
    description: 'School plans, Stripe sync, cache refresh, and subscription cleanup.',
  },
  {
    key: 'maintenance',
    label: 'Maintenance',
    icon: 'build',
    description: 'Backfills, framework seeding, and workflow diagnostics.',
  },
] as const satisfies readonly SettingsSectionMeta[];

export const settingsSectionDescription = (section: SettingsSection): string =>
  SETTINGS_SECTIONS.find((item) => item.key === section)?.description || '';
